import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { HttpService, ObservableCollection, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { InventoryStocktakingDocumentItemModel, InventoryStocktakingDocumentModel, InventoryStocktakingGetDocumentDTO, InventoryStocktakingUpdateDocumentDTO } from '@skylab-shared/dataModel';
import { getStartOfDate, MS_PER_MINUTE, pickBy } from '@skylab-shared/utils';
import { InventoryStocktakingDocumentBO } from './inventory-stocktaking-document-bo';

@Repository({
    boType: InventoryStocktakingDocumentBO,
    apiController: TaggedUrl.join('inventory/stocktaking', [Tags.Api]),
    customSaveRequestTimeout: 3 * MS_PER_MINUTE,
    dto: {
        get: InventoryStocktakingGetDocumentDTO,
        update: InventoryStocktakingUpdateDocumentDTO
    }
})
@Injectable({ providedIn: 'root' })
export class InventoryStocktakingDocumentRepository extends RepositoryBase<InventoryStocktakingDocumentBO, InventoryStocktakingDocumentModel, InventoryStocktakingGetDocumentDTO, InventoryStocktakingUpdateDocumentDTO> {

    public constructor(
        private readonly queryBus: QueryBus,
        private readonly weaver: Weaver,
        http: Http
    ) {
        super(http as HttpService);
    }

    protected createBO(): InventoryStocktakingDocumentBO {
        return new InventoryStocktakingDocumentBO(this.weaver as WeaverBase, this.queryBus as any);
    }

    protected async preparePlainObjectAsync(model: InventoryStocktakingDocumentModel): Promise<InventoryStocktakingUpdateDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            documentNumber: model.documentNumber,
            numberingId: model.numberingId,
            stocktakingDate: getStartOfDate(model.issueDate),
            inventoryId: model.inventoryId,
            preparer: model.preparer,
            approver: model.approver,
            items: model.items.map(elm => ({
                no: elm.no,
                productName: elm.productName,
                productId: elm.productId,
                unitId: elm.unitId,
                systemQuantity: elm.systemQuantity,
                stocktakingQuantity: elm.stocktakingQuantity,
                receiptQuantity: elm.receiptQuantity,
                issueQuantity: elm.issueQuantity,
                salePrice: elm.salePrice,
                purchasePrice: elm.purchasePrice,
                symbol: elm.symbol,
                barCode: elm.barCode
            })),
        };
    }

    protected async fromPlainObjectAsync(item: InventoryStocktakingGetDocumentDTO): Promise<InventoryStocktakingDocumentModel> {
        return new InventoryStocktakingDocumentModel(pickBy<InventoryStocktakingDocumentModel>({
            duplicatePreventionId: item.duplicatePreventionId,
            version: item.version,
            id: item.id,
            documentNumber: item.documentNumber,
            numberingId: item.numberingId,
            issueDate: item.stocktakingDate,
            inventoryId: item.inventoryId,
            preparer: item.preparer,
            approver: item.approver,
            inventoryEffect: item.inventoryEffect,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new InventoryStocktakingDocumentItemModel(pickBy<Partial<InventoryStocktakingDocumentItemModel>>({
                    no: documentItem.no,
                    productName: documentItem.productName,
                    productId: documentItem.productId,
                    unitId: documentItem.unitId,
                    systemQuantity: documentItem.systemQuantity,
                    stocktakingQuantity: documentItem.stocktakingQuantity,
                    receiptQuantity: documentItem.receiptQuantity,
                    issueQuantity: documentItem.issueQuantity,
                    salePrice: documentItem.salePrice,
                    purchasePrice: documentItem.purchasePrice,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode
                }, val => val !== void 0));
            }))
        }, val => val !== void 0));
    }
}
